/**
 * Export one baseline from a Performance report as a single-sheet .xlsx.
 *
 * The sheet mirrors the report rather than the stored fit: the row key is whichever channel the
 * page has on X (TWS or Bsp), and the columns are the report's own chart channels, named exactly as
 * the report names them. Only AVG curves are written -- STD/AAV/RMS are chart decoration, not
 * numbers anyone reads out of a table.
 *
 * Upwind rows come first, then one blank row, then downwind, sharing the single header row.
 *
 * Deliberately one baseline -- the one whose picker was opened -- not every baseline on screen.
 * Two baselines cannot share an X column honestly: on a Bsp axis they have entirely different X
 * values, and even on TWS their grids cover different ranges, so the union leaves ragged blanks
 * across half the sheet. Writing a second block also forces every header to carry a
 * "name - config - " prefix, which buries the channel name the reader is actually looking for.
 * Comparing two baselines is what the chart is for; the sheet is for reading one set of numbers.
 *
 * Values are written exactly as stored (kph for speeds, degrees for angles), matching what the
 * charts draw -- the unit lives in the channel name, so no conversion is applied here.
 */

import type { BaselineFitJson, BaselineLegKey } from '../services/baselinesService';
import { readFitValue } from './baselineCurve';
import { error as logError } from './console';
import { round } from './global';

/** Legs in the order they are stacked on the sheet. */
const LEG_ORDER: BaselineLegKey[] = ['UPWIND', 'DOWNWIND'];

/** The only aggregate exported. See the file comment. */
const AGR = 'AVG';

/** Decimals kept per cell. The fits are smooth curves, so this is display precision, not data loss. */
const VALUE_DECIMALS = 3;

/** One baseline as it is drawn on the report: a fit, the config shown, and its label. */
export interface BaselineExcelSeries {
  name: string;
  /** Which CONFIG within the fit is on screen. */
  config: string;
  fit: BaselineFitJson | null | undefined;
}

export interface BaselineExcelExportArgs {
  /** The baseline to write: the one whose picker was opened. */
  baseline: BaselineExcelSeries;
  /** Channel on the report's x-axis, e.g. `Tws_kph` or `Bsp_kph`. */
  xChannel: string;
  /** The report's chart channels, in chart order, already deduplicated. */
  channels: string[];
}

/** Nothing to write without both a fit and the config to read out of it. */
export function canExport(args: BaselineExcelExportArgs): boolean {
  return !!args.baseline?.fit && !!args.baseline?.config;
}

/**
 * Column headers: the x channel, then the report's channels under their own names.
 *
 * Unprefixed on purpose — the file name already says which baseline and config this is, so
 * repeating it on all thirty-odd columns would only push the channel name out of view.
 */
export function baselineExcelHeaders(args: BaselineExcelExportArgs): string[] {
  return [args.xChannel, ...args.channels];
}

/** One output row: the x value plus one cell per channel column, null where there is no coverage. */
export interface BaselineExcelRow {
  x: number;
  values: Array<number | null>;
}

/**
 * Rows for one leg: walk the baseline's own wind grid, read X and every channel at each point.
 *
 * Sorted by X because grid order is only ascending when X is TWS; on a Bsp axis it is not. A grid
 * point with no X value is skipped outright — there is nothing to place the row against, and the
 * chart skips the same points. A row whose channels are all blank is dropped too: it would be an x
 * value the report draws nothing at.
 */
export function baselineExcelRowsForLeg(
  args: BaselineExcelExportArgs,
  leg: BaselineLegKey
): BaselineExcelRow[] {
  const { baseline } = args;
  const legFit = baseline?.fit?.configs?.[baseline.config]?.[leg] || null;
  const grid = baseline?.fit?.tws_grid;
  if (!legFit || !Array.isArray(grid)) { return []; }

  const rows: BaselineExcelRow[] = [];
  for (let i = 0; i < grid.length; i++) {
    const x = readFitValue(legFit, args.xChannel, AGR, i);
    if (x == null) { continue; }
    const values = args.channels.map((channel) => readFitValue(legFit, channel, AGR, i));
    if (values.every((v) => v == null)) { continue; }
    rows.push({ x, values });
  }
  rows.sort((a, b) => a.x - b.x);
  return rows;
}

/** Excel worksheet names: max 31 chars, no \ / * ? : [ ] characters. */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[\\/*?:[\]]/g, ' ').trim();
  return (cleaned === '' ? 'Baselines' : cleaned).slice(0, 31);
}

function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned === '' ? 'Baselines' : cleaned;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const HEADER_FILL = 'FFEFEFEF';
const BORDER_COLOR = 'FF808080';

/** The baseline and config name the file, since neither appears in the sheet itself. */
function exportFileName(baseline: BaselineExcelSeries): string {
  return `${sanitizeFileName(baseline.name)} - ${sanitizeFileName(baseline.config)} - Baseline.xlsx`;
}

/**
 * Write the baseline to a .xlsx download.
 *
 * Returns a result rather than throwing so the picker can show the failure inline next to the
 * button that caused it.
 */
export async function exportBaselineExcel(
  args: BaselineExcelExportArgs
): Promise<{ success: boolean; message?: string }> {
  try {
    if (!args.xChannel) {
      return { success: false, message: 'No chart x-axis to export against.' };
    }
    if (args.channels.length === 0) {
      return { success: false, message: 'The report has no chart channels to export.' };
    }
    if (!canExport(args)) {
      return { success: false, message: 'Select a baseline and configuration first.' };
    }

    const headers = baselineExcelHeaders(args);
    const upwind = baselineExcelRowsForLeg(args, LEG_ORDER[0]);
    const downwind = baselineExcelRowsForLeg(args, LEG_ORDER[1]);
    if (upwind.length === 0 && downwind.length === 0) {
      return { success: false, message: 'No fitted data for these channels on this x-axis.' };
    }

    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sanitizeSheetName('Baselines'), {
      views: [{ showGridLines: false, state: 'frozen', ySplit: 1 }],
    });

    for (let c = 1; c <= headers.length; c++) {
      sheet.getColumn(c).width = Math.max(12, Math.min(28, headers[c - 1].length + 2));
    }

    const thinBorder = {
      top: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
      left: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
      bottom: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
      right: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    };
    const center = { horizontal: 'center' as const, vertical: 'middle' as const };

    for (let c = 1; c <= headers.length; c++) {
      const cell = sheet.getCell(1, c);
      cell.value = headers[c - 1];
      cell.font = { bold: true, size: 10 };
      cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: HEADER_FILL } };
      cell.alignment = center;
      cell.border = thinBorder;
    }
    sheet.getRow(1).height = 18;

    // Upwind, one blank row, then downwind. The blank row is the only separator by request.
    let rowIndex = 2;
    const writeRows = (rows: BaselineExcelRow[]) => {
      for (const row of rows) {
        const cells = [round(row.x, VALUE_DECIMALS), ...row.values.map((v) => (v == null ? null : round(v, VALUE_DECIMALS)))];
        for (let c = 1; c <= headers.length; c++) {
          const cell = sheet.getCell(rowIndex, c);
          const v = cells[c - 1];
          if (v != null) { cell.value = v; }
          cell.alignment = center;
          cell.border = thinBorder;
        }
        rowIndex++;
      }
    };

    writeRows(upwind);
    if (upwind.length > 0 && downwind.length > 0) { rowIndex++; }
    writeRows(downwind);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(blob, exportFileName(args.baseline));
    return { success: true };
  } catch (err) {
    logError('[baselineExcel] export failed', err);
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Failed to export Excel file',
    };
  }
}
